/**
 * ChatLayout - Layout de 3 painéis do chat
 * Sidebar Left | Main Box | Sidebar Right
 */

import React, { useState, useCallback, useEffect, useRef } from 'react';
import ChatSidebarLeft from './ChatSidebarLeft';
import ChatBox from './ChatBox';
import ChatSidebarRight from './ChatSidebarRight';
import { ChatDashboard } from './ChatDashboard';
import { useChatPreferences } from '../hooks';
import type { SidebarView } from '../types';

/**
 * Hook: viewport < 1024px (breakpoint `lg`) — chat mobile tem anatomia própria
 * (lista full-screen + thread slide-over). matchMedia com listener, sem resize thrash.
 */
function useIsMobileChat(): boolean {
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(max-width: 1023px)').matches
  );
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 1023px)');
    const onChange = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  return isMobile;
}

interface ChatLayoutProps {
  selectedConversationId: string | null;
  onSelectConversation: (id: string) => void;
  onClearSelection: () => void;
  initialView: SidebarView | null;
}

/**
 * Estado para passar o termo de busca da sidebar para o ChatBox
 * Quando o usuário busca na sidebar e clica em uma conversa,
 * o termo de busca é propagado para abrir automaticamente a busca interna
 */

const ChatLayout: React.FC<ChatLayoutProps> = ({
  selectedConversationId,
  onSelectConversation,
  onClearSelection,
  initialView,
}) => {
  // Chat preferences (persisted in localStorage)
  const {
    leftSidebarExpanded,
    setLeftSidebarExpanded,
    rightSidebarOpen,
    setRightSidebarOpen
  } = useChatPreferences();

  // Mobile: lista sempre expandida (o modo rail w-16 é conceito desktop)
  const isMobile = useIsMobileChat();

  // Estado para o termo de busca vindo da sidebar
  const [sidebarSearchTerm, setSidebarSearchTerm] = useState<string>('');

  // Wrapper callback que captura o termo de busca ao selecionar conversa
  const handleSelectConversation = useCallback((id: string, searchTerm?: string) => {
    setSidebarSearchTerm(searchTerm || '');
    onSelectConversation(id);
  }, [onSelectConversation]);

  const toggleLeftSidebar = () => {
    setLeftSidebarExpanded(prev => !prev);
  };

  const toggleRightSidebar = () => {
    setRightSidebarOpen(prev => !prev);
  };

  // #112 — Detalhes vira overlay flutuante quando a região thread+detalhes fica
  // estreita (mockup: `@container (max-width:1080px)` no `.cmain`). Medimos a
  // largura do layout e descontamos a coluna LISTA (340 expandida / 64 rail).
  const layoutRef = useRef<HTMLDivElement>(null);
  const [layoutWidth, setLayoutWidth] = useState(0);
  useEffect(() => {
    const el = layoutRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver((entries) => {
      setLayoutWidth(entries[0]?.contentRect.width ?? 0);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const leftColWidth = isMobile ? 0 : (leftSidebarExpanded ? 340 : 64);
  const detailsOverlay = !isMobile && layoutWidth > 0 && (layoutWidth - leftColWidth) <= 1080;

  return (
    <div
      ref={layoutRef}
      data-testid="chat-layout"
      className="relative flex h-full w-full min-w-0 min-h-0"
      style={{ backgroundColor: 'var(--theme-page-background)' }}
    >
      {/* LEFT SIDEBAR — coluna LISTA (abas IA/Chats/Grupos/Contatos/Histórico).
          Mobile (<lg): ocupa a tela inteira (anatomia própria do mockup). */}
      <div
        data-testid="chat-layout-sidebar-left"
        className={`w-full min-h-0 relative z-10 overflow-visible lg:flex-shrink-0 lg:transition-all lg:duration-300 ${
          leftSidebarExpanded ? 'lg:w-[340px]' : 'lg:w-16'
        }`}
      >
        <ChatSidebarLeft
          expanded={isMobile ? true : leftSidebarExpanded}
          onToggle={toggleLeftSidebar}
          selectedConversationId={selectedConversationId}
          onSelectConversation={handleSelectConversation}
          initialView={initialView}
        />
      </div>

      {/* MAIN BOX — coluna THREAD / DASHBOARD.
          Mobile (<lg): slide-over full-screen sobre a lista (`.m-thread` do mockup). */}
      <div
        data-testid="chat-layout-main"
        className={`flex flex-col min-w-0 min-h-0 inset-0 transition-transform duration-300 ease-out max-[899px]:fixed max-[899px]:z-[55] min-[900px]:absolute min-[900px]:z-30 ${
          selectedConversationId ? 'translate-x-0' : 'translate-x-full'
        } lg:static lg:z-auto lg:flex-1 lg:translate-x-0 lg:transition-none max-lg:[background:var(--theme-page-background-color)] lg:[background-color:var(--theme-page-background)]`}
      >
        {selectedConversationId ? (
          <ChatBox
            conversationId={selectedConversationId}
            onToggleRightSidebar={toggleRightSidebar}
            rightSidebarOpen={rightSidebarOpen}
            onClose={onClearSelection}
            onSelectConversation={handleSelectConversation}
            initialSearchTerm={sidebarSearchTerm}
          />
        ) : (
          <ChatDashboard
            onFilterChange={(filter) => {
              // TODO: Implementar lógica de filtro na sidebar
              console.log('Filter changed:', filter);
            }}
          />
        )}
      </div>

      {/* Scrim mobile do sheet de detalhes (`.m-scrim` do mockup) — dimmer visual
          apenas: detalhe da conversa é superfície de CONTEÚDO, não fecha ao tocar
          fora (DS §Overlays dismissal); fechar fica pelo botão do sheet */}
      {selectedConversationId && rightSidebarOpen && (
        <div
          className="fixed inset-0 z-[60] lg:hidden"
          style={{ backgroundColor: 'rgba(15, 23, 42, 0.42)' }}
          data-testid="chat-details-scrim"
        />
      )}

      {/* RIGHT SIDEBAR — coluna no desktop, bottom sheet no mobile */}
      {selectedConversationId && (
        <ChatSidebarRight
          conversationId={selectedConversationId}
          isOpen={rightSidebarOpen}
          onClose={toggleRightSidebar}
          overlayMode={detailsOverlay}
        />
      )}
    </div>
  );
};

export default ChatLayout;
